import { useRef, useState, useEffect, useMemo } from 'react';
import { Info } from 'react-feather';
import { Paperclip, ArrowUp, Sparkles, Wrench, Download, ChevronDown, Globe, LayoutTemplate, Palette } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import { useTranslation } from 'react-i18next';
import GeminiLogo from '@/assets/provider-logos/google.svg?react';
import ClaudeLogo from '@/assets/provider-logos/anthropic.svg?react';
import OpenAILogo from '@/assets/provider-logos/openai.svg?react';

import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu"
import {
	type AgentMode,
} from '../components/agent-mode-toggle';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { usePaginatedApps } from '@/hooks/use-paginated-apps';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { AppCard } from '@/components/shared/AppCard';
import clsx from 'clsx';
import { useImageUpload } from '@/hooks/use-image-upload';
import { useDragDrop } from '@/hooks/use-drag-drop';
import { ImageAttachmentPreview } from '@/components/image-attachment-preview';
import { SUPPORTED_IMAGE_MIME_TYPES } from '@/api-types';
import { Button } from '@/components/ui/button';

export default function Home() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { requireAuth } = useAuthGuard();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [agentMode] = useState<AgentMode>('deterministic');
	const [query, setQuery] = useState('');
	const { user } = useAuth();
	const [selectedModel, setSelectedModel] = useState('auto');
	const modelNameMap: Record<string, string> = {
		'auto': 'Auto',
		'gemini-3-pro': 'Gemini 3 Pro',
		'claude-3.5-sonnet': 'Claude 3.5 Sonnet',
		'gpt-5': 'GPT-5'
	};

	const { images, addImages, removeImage, clearImages } = useImageUpload({
		onError: (error) => {
			// TODO: Show error toast/notification
			console.error('Image upload error:', error);
		},
	});

	const { isDragging, dragHandlers } = useDragDrop({
		onFilesDropped: addImages,
		accept: [...SUPPORTED_IMAGE_MIME_TYPES],
	});


	const placeholderPhrases = useMemo(() => [
		t('home.placeholderPhrases.todo'),
		t('home.placeholderPhrases.f1'),
		t('home.placeholderPhrases.finance')
	], [t]);
	const [currentPlaceholderPhraseIndex, setCurrentPlaceholderPhraseIndex] = useState(0);
	const [currentPlaceholderText, setCurrentPlaceholderText] = useState("");
	const [isPlaceholderTyping, setIsPlaceholderTyping] = useState(true);

	const {
		apps,
		loading,
	} = usePaginatedApps({
		type: 'public',
		defaultSort: 'popular',
		defaultPeriod: 'week',
		limit: 6,
	});

	// Discover section should appear only when enough apps are available and loading is done
	const discoverReady = useMemo(() => !loading && (apps?.length ?? 0) > 5, [loading, apps]);

	const handleCreateApp = (query: string, mode: AgentMode) => {
		const encodedQuery = encodeURIComponent(query);
		const encodedMode = encodeURIComponent(mode);
		const encodedModel = encodeURIComponent(selectedModel);
		
		// Store images in sessionStorage to avoid URL length limits
		if (images.length > 0) {
			try {
				sessionStorage.setItem('pending_chat_images', JSON.stringify(images));
			} catch (e) {
				console.error('Failed to save images to session storage', e);
				// Fallback or alert user? For now just log.
			}
		}
		
		const intendedUrl = `/chat/new?query=${encodedQuery}&agentMode=${encodedMode}&model=${encodedModel}`;

		if (
			!requireAuth({
				requireFullAuth: true,
				actionContext: 'to create applications',
				intendedUrl: intendedUrl,
			})
		) {
			return;
		}

		// User is already authenticated, navigate immediately
		navigate(intendedUrl);
		// Clear images after navigation
		clearImages();
	};

	// Auto-resize textarea based on content
	const adjustTextareaHeight = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			const scrollHeight = textareaRef.current.scrollHeight;
			const maxHeight = 300; // Maximum height in pixels
			textareaRef.current.style.height =
				Math.min(scrollHeight, maxHeight) + 'px';
		}
	};

	useEffect(() => {
		adjustTextareaHeight();
	}, []);

	// Typewriter effect
	useEffect(() => {
		const currentPhrase = placeholderPhrases[currentPlaceholderPhraseIndex];

		if (isPlaceholderTyping) {
			if (currentPlaceholderText.length < currentPhrase.length) {
				const timeout = setTimeout(() => {
					setCurrentPlaceholderText(currentPhrase.slice(0, currentPlaceholderText.length + 1));
				}, 100); // Typing speed
				return () => clearTimeout(timeout);
			} else {
				// Pause before erasing
				const timeout = setTimeout(() => {
					setIsPlaceholderTyping(false);
				}, 2000); // Pause duration
				return () => clearTimeout(timeout);
			}
		} else {
			if (currentPlaceholderText.length > 0) {
				const timeout = setTimeout(() => {
					setCurrentPlaceholderText(currentPlaceholderText.slice(0, -1));
				}, 50); // Erasing speed
				return () => clearTimeout(timeout);
			} else {
				// Move to next phrase
				setCurrentPlaceholderPhraseIndex((prev) => (prev + 1) % placeholderPhrases.length);
				setIsPlaceholderTyping(true);
			}
		}
	}, [currentPlaceholderText, currentPlaceholderPhraseIndex, isPlaceholderTyping, placeholderPhrases]);

	const discoverLinkRef = useRef<HTMLDivElement>(null);
    const { i18n } = useTranslation();

	return (
		<div className="relative flex flex-col items-center size-full" dir={i18n.language === 'ar-SA' ? 'rtl' : 'ltr'}>
			{/* Optimized CSS-based dotted background for better LCP - replaces SVG */}
			<div
				className="fixed inset-0 text-[#064e3b] z-0 opacity-20 pointer-events-none dotted-bg"
				style={{
					backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
					backgroundSize: '12px 12px'
				}}
			/>
			
			<LayoutGroup>
				<div className="rounded-md w-full max-w-2xl overflow-hidden">
					<motion.div
						layout
						transition={{ layout: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }}
						className={clsx(
							"px-6 p-8 flex flex-col items-center z-10 lcp-optimized w-full max-w-3xl",
							discoverReady ? "mt-32" : "mt-[15vh] sm:mt-[20vh]"
						)}>
						
						{/* Gemini Pill */}
						<div className="mb-8 px-4 py-1.5 rounded-full bg-gradient-to-r from-gray-800/80 to-gray-900/80 border border-white/10 backdrop-blur-md flex items-center gap-3 shadow-lg animate-in fade-in zoom-in duration-500">
							<Sparkles className="w-4 h-4 text-blue-400" />
							<span className="text-sm font-medium text-gray-200 tracking-wide">{t('home.model.gemini3.label')}</span>
							<Button variant="secondary" size="sm" className="h-6 px-3 text-xs bg-white text-black hover:bg-gray-200 rounded-full font-semibold transition-colors">
								{t('home.model.gemini3.cta')}
							</Button>
						</div>

						<h1
							className="flex items-center justify-center mb-6 animate-in slide-in-from-bottom-4 duration-700"
						>
							<div className="flex items-center hero-logo-container" dir="ltr">
								<img
									src="/v-logo.svg"
									alt="V"
									className="h-[4.5rem] w-auto drop-shadow-sm"
									width="72"
									height="72"
									fetchPriority="high"
								/>
								<span className="text-7xl font-bold text-[#064e3b] tracking-tight drop-shadow-sm -ml-3">ibesaudi</span>
							</div>
						</h1>
						
						<p className="text-xl text-text-tertiary mb-12 font-light text-center max-w-xl leading-relaxed animate-in slide-in-from-bottom-6 duration-700 delay-100">
							{t('home.tagline')}
						</p>

						<form
							method="POST"
							onSubmit={(e) => {
								e.preventDefault();
								handleCreateApp(query, agentMode);
							}}
							className="flex z-10 flex-col w-full bg-bg-2 border border-border-primary/20 rounded-[24px] shadow-2xl overflow-hidden transition-all duration-200 ring-1 ring-border-primary/5 animate-in slide-in-from-bottom-8 duration-700 delay-200"
						>
							{/* Input Header Controls */}
							<div className="flex items-center gap-2 px-4 pt-4">
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button type="button" variant="ghost" size="sm" className="h-8 gap-2 rounded-full bg-bg-3/50 hover:bg-bg-3 text-text-secondary hover:text-text-primary font-normal transition-colors">
											<Wrench className="w-4 h-4" />
											{t('home.tools')}
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="start" className="w-56 bg-bg-2 border-border-primary/20 text-text-primary">
										<DropdownMenuItem className="hover:bg-bg-3 hover:text-text-primary cursor-pointer">
											<Globe className="mr-2 h-4 w-4" />
											<span>{t('home.menu.clone')}</span>
										</DropdownMenuItem>
										<DropdownMenuItem className="hover:bg-bg-3 hover:text-text-primary cursor-pointer">
											<LayoutTemplate className="mr-2 h-4 w-4" />
											<span>{t('home.menu.template')}</span>
										</DropdownMenuItem>
										<DropdownMenuItem className="hover:bg-bg-3 hover:text-text-primary cursor-pointer">
											<Palette className="mr-2 h-4 w-4" />
											<span>{t('home.menu.theme')}</span>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>

								<Button type="button" variant="ghost" size="sm" className="h-8 gap-2 rounded-full bg-bg-3/50 hover:bg-bg-3 text-text-secondary hover:text-text-primary font-normal transition-colors">
									<Download className="w-4 h-4" />
									{t('home.import')}
								</Button>
							</div>

							<div 
								className={clsx(
									"flex-1 flex flex-col relative p-5",
									isDragging && "ring-2 ring-accent ring-offset-2 rounded-lg"
								)}
								{...dragHandlers}
							>
								{isDragging && (
									<div className="absolute inset-0 flex items-center justify-center bg-accent/10 backdrop-blur-sm rounded-lg z-30 pointer-events-none">
										<p className="text-accent font-medium">{t('home.dropImages')}</p>
									</div>
								)}
								{/* Animated Placeholder */}
								{(!query || query.length === 0) && (
									<div className="absolute top-5 left-5 rtl:right-5 pointer-events-none select-none text-text-tertiary text-xl font-light leading-relaxed tracking-wide">
										{currentPlaceholderText}
									</div>
								)}
								<textarea
									className="w-full min-h-[80px] bg-transparent resize-none ring-0 outline-0 text-text-primary text-xl font-light leading-relaxed tracking-wide relative z-20"
									name="query"
									value={query}
									ref={textareaRef}
									onChange={(e) => {
										setQuery(e.target.value);
										adjustTextareaHeight();
									}}
									onInput={adjustTextareaHeight}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey) {
											e.preventDefault();
											handleCreateApp(query, agentMode);
										}
									}}
								/>
								{images.length > 0 && (
									<div className="mt-3">
										<ImageAttachmentPreview
											images={images}
											onRemove={removeImage}
										/>
									</div>
								)}
							</div>

							{/* Input Footer Controls */}
							<div className="flex items-center justify-between px-4 pb-4">
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={() => document.getElementById('hidden-file-input')?.click()}
										className="p-2 rounded-full hover:bg-bg-3 text-text-secondary hover:text-text-primary transition-colors"
									>
										<Paperclip className="w-5 h-5" strokeWidth={2} />
									</button>
									<input
										id="hidden-file-input"
										type="file"
										accept={SUPPORTED_IMAGE_MIME_TYPES.join(',')}
										multiple
										onChange={(e) => {
											const files = Array.from(e.target.files || []);
											if (files.length > 0) addImages(files);
											e.target.value = '';
										}}
										className="hidden"
									/>
								</div>

								<div className="flex items-center justify-end gap-3">
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<div className="flex items-center gap-2 bg-bg-3/50 rounded-full pl-3 pr-2 py-1.5 border border-border-primary/10 cursor-pointer hover:bg-bg-3 transition-colors group">
												<Sparkles className="w-4 h-4 text-indigo-400 group-hover:text-indigo-300 transition-colors" />
												<span className="text-sm text-text-secondary font-medium group-hover:text-text-primary transition-colors">
													{modelNameMap[selectedModel] || "Auto"}
												</span>
												<ChevronDown className="w-3 h-3 text-text-tertiary group-hover:text-text-secondary transition-colors" />
											</div>
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end" className="w-64 bg-bg-2 border-border-primary/20 text-text-primary">
										<DropdownMenuLabel className="text-xs text-text-tertiary font-normal uppercase tracking-wider">{t('home.model.selectionLabel')}</DropdownMenuLabel>
										<DropdownMenuRadioGroup value={selectedModel} onValueChange={setSelectedModel}>
											<DropdownMenuRadioItem value="auto" className="hover:bg-bg-3 hover:text-text-primary cursor-pointer">
												<div className="flex flex-col gap-1">
													<div className="flex items-center gap-2">
														<Sparkles className="w-3.5 h-3.5 text-indigo-400" />
														<span className="font-medium">{t('home.model.auto.label')}</span>
													</div>
													<span className="text-xs text-text-secondary">{t('home.model.auto.description')}</span>
												</div>
											</DropdownMenuRadioItem>
											<DropdownMenuSeparator className="bg-border-primary/10" />
											<DropdownMenuRadioItem value="gemini-3-pro" className="hover:bg-bg-3 hover:text-text-primary cursor-pointer">
												<div className="flex flex-col gap-1">
													<div className="flex items-center gap-2">
														<GeminiLogo className="w-3.5 h-3.5" />
														<span className="font-medium">{t('home.model.gemini3Pro.label')}</span>
													</div>
													<span className="text-xs text-text-secondary">{t('home.model.gemini3Pro.description')}</span>
												</div>
											</DropdownMenuRadioItem>
											<DropdownMenuRadioItem value="claude-3.5-sonnet" className="hover:bg-bg-3 hover:text-text-primary cursor-pointer">
												<div className="flex flex-col gap-1">
													<div className="flex items-center gap-2">
														<ClaudeLogo className="w-3.5 h-3.5" />
														<span className="font-medium">{t('home.model.claude.label')}</span>
													</div>
													<span className="text-xs text-text-secondary">{t('home.model.claude.description')}</span>
												</div>
											</DropdownMenuRadioItem>
											<DropdownMenuRadioItem value="gpt-5" className="hover:bg-bg-3 hover:text-text-primary cursor-pointer">
												<div className="flex flex-col gap-1">
													<div className="flex items-center gap-2">
														<OpenAILogo className="w-3.5 h-3.5" />
														<span className="font-medium">{t('home.model.gpt5.label')}</span>
													</div>
													<span className="text-xs text-text-secondary">{t('home.model.gpt5.description')}</span>
												</div>
											</DropdownMenuRadioItem>
										</DropdownMenuRadioGroup>
									</DropdownMenuContent>
									</DropdownMenu>
									
									<Button
										type="submit"
										disabled={!query.trim()}
										className="rounded-full w-10 h-10 p-0 bg-white hover:bg-gray-200 text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl active:scale-95"
									>
										<ArrowUp className="w-5 h-5" strokeWidth={2.5} />
									</Button>
								</div>
							</div>
						</form>
					</motion.div>

				</div>

				<AnimatePresence>
					{images.length > 0 && (
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -10 }}
							className="w-full max-w-2xl px-6"
						>
							<div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-bg-4/50 dark:bg-bg-2/50 border border-accent/20 dark:border-accent/30 shadow-sm">
								<Info className="size-4 text-accent flex-shrink-0 mt-0.5" />
								<p className="text-xs text-text-tertiary leading-relaxed">
									<span className="font-medium text-text-secondary">Images Beta:</span> Images guide app layout and design but may not be replicated exactly. The coding agent cannot access images directly for app assets.
								</p>
							</div>
						</motion.div>
					)}
				</AnimatePresence>

				<AnimatePresence>
					{discoverReady && (
						<motion.section
							key="discover-section"
							layout
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
							className={clsx('max-w-6xl mx-auto px-4 z-10', images.length > 0 ? 'mt-10' : 'mt-16 mb-8')}
						>
							<div className='flex flex-col items-start'>
								<h2 className="text-2xl font-medium text-text-secondary/80">{t('home.discoverApps')}</h2>
								<div ref={discoverLinkRef} className="text-md font-light mb-4 text-text-tertiary hover:underline underline-offset-4 select-text cursor-pointer" onClick={() => navigate('/discover')} >View All</div>
								<motion.div
									layout
									transition={{ duration: 0.4 }}
									className="grid grid-cols-2 xl:grid-cols-3 gap-6"
								>
									<AnimatePresence mode="popLayout">
										{apps.map((app, index) => (
											<AppCard
												key={app.id}
												app={app}
												index={index}
												priority={index < 3 ? 'high' : 'low'}
												onClick={() => navigate(`/app/${app.id}`)}
												showStats={true}
												showUser={true}
												showActions={false}
											/>
										))}
									</AnimatePresence>
								</motion.div>
							</div>
						</motion.section>
					)}
				</AnimatePresence>
			</LayoutGroup>

			{/* Nudge towards Discover */}
			{user && <CurvedArrow sourceRef={discoverLinkRef} target={{ x: 50, y: window.innerHeight - 60 }} />}
		</div>
	);
}



type ArrowProps = {
	/** Ref to the source element the arrow starts from */
	sourceRef: React.RefObject<HTMLElement | null>;
	/** Target point in viewport/client coordinates */
	target: { x: number; y: number };
	/** Curve intensity (0.1 - 1.5 is typical) */
	curvature?: number;
	/** Optional pixel offset from source element edge */
	sourceOffset?: number;
	/** If true, hides the arrow when the source is offscreen/not measurable */
	hideWhenInvalid?: boolean;
};

type Point = { x: number; y: number };

export const CurvedArrow: React.FC<ArrowProps> = ({
	sourceRef,
	target,
	curvature = 0.5,
	sourceOffset = 6,
	hideWhenInvalid = true,
}) => {
	const [start, setStart] = useState<Point | null>(null);
	const [end, setEnd] = useState<Point | null>(null);

	const rafRef = useRef<number | null>(null);
	const roRef = useRef<ResizeObserver | null>(null);

	const compute = () => {
		const el = sourceRef.current;
		if (!el) {
			setStart(null);
			setEnd(null);
			return;
		}

		const rect = el.getBoundingClientRect();
		if (!rect || rect.width === 0 || rect.height === 0) {
			setStart(null);
			setEnd(null);
			return;
		}

		const endPoint: Point = { x: target.x, y: target.y };

		// Choose an anchor on the source: midpoint of the side facing the target
		const centers = {
			right: { x: rect.right, y: rect.top + rect.height / 2 },
			left: { x: rect.left, y: rect.top + rect.height / 2 },
		};

		// Distances to target from each side center
		const dists = Object.fromEntries(
			Object.entries(centers).map(([side, p]) => [
				side,
				(p.x - endPoint.x) ** 2 + (p.y - endPoint.y) ** 2,
			])
		) as Record<keyof typeof centers, number>;

		const bestSide = (Object.entries(dists).sort((a, b) => a[1] - b[1])[0][0] ||
			"right") as keyof typeof centers;

		// Nudge start point slightly outside the element for visual clarity
		const nudge = (p: Point, side: keyof typeof centers, offset: number) => {
			switch (side) {
				case "right":
					return { x: p.x + offset, y: p.y };
				case "left":
					return { x: p.x - offset, y: p.y };
			}
		};

		const startPoint = nudge(centers[bestSide], bestSide, sourceOffset);

		setStart(startPoint);
		setEnd(endPoint);
	};

	// Throttle updates with rAF to avoid layout thrash
	const scheduleCompute = () => {
		if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
		rafRef.current = requestAnimationFrame(compute);
	};

	useEffect(() => {
		scheduleCompute();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [target.x, target.y, sourceRef.current]);

	useEffect(() => {
		const onScroll = () => scheduleCompute();
		const onResize = () => scheduleCompute();

		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onResize);

		// Track source element size changes
		const el = sourceRef.current;
		if ("ResizeObserver" in window) {
			roRef.current = new ResizeObserver(() => scheduleCompute());
			if (el) roRef.current.observe(el);
		}

		scheduleCompute();

		return () => {
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onResize);
			if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
			if (roRef.current && el) roRef.current.unobserve(el);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const d = useMemo(() => {
		if (!start || !end) return "";

		const dx = end.x - start.x;
		const dy = end.y - start.y;

		// Control points: bend the curve based on the primary axis difference.
		// This gives a nice S or C curve without sharp kinks.
		const cpOffset = Math.max(Math.abs(dx), Math.abs(dy)) * curvature;

		const c1: Point = { x: start.x + cpOffset * (dx >= 0 ? 1 : -1), y: start.y };
		const c2: Point = { x: end.x - cpOffset * (dx >= 0 ? 1 : -1), y: end.y };

		return `M ${start.x},${start.y} C ${c1.x},${c1.y} ${c2.x},${c2.y} ${end.x},${end.y}`;
	}, [start, end, curvature]);

	const hidden = hideWhenInvalid && (!start || !end);

	if (start && end && (end.y - start.y > 420 || start.x - end.x < 100)) {
		return null;
	}

	return (
		<svg
			aria-hidden="true"
			style={{
				position: "fixed",
				inset: 0,
				width: "100vw",
				height: "100vh",
				pointerEvents: "none",
				overflow: "visible",
				zIndex: 9999,
				display: hidden ? "none" : "block",
			}}
		>
			<defs>
				<filter id="discover-squiggle" x="-20%" y="-20%" width="140%" height="140%">
					<feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="1" seed="3" result="noise" />
					<feDisplacementMap in="SourceGraphic" in2="noise" scale="1" xChannelSelector="R" yChannelSelector="G" />
				</filter>
				<marker id="discover-arrowhead" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="strokeWidth" opacity={0.20}>
					<path d="M 0 1.2 L 7 4" stroke="var(--color-text-tertiary)" strokeWidth="1.6" strokeLinecap="round" fill="none" />
					<path d="M 0 6.8 L 7 4" stroke="var(--color-text-tertiary)" strokeWidth="1.2" strokeLinecap="round" fill="none" />
				</marker>
			</defs>

			<path
				d={d}
				// stroke="var(--color-accent)"
				stroke="var(--color-text-tertiary)"
				strokeOpacity={0.20}
				strokeWidth={1.6}
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
				vectorEffect="non-scaling-stroke"
				markerEnd="url(#discover-arrowhead)"
			/>
			{/* Soft squiggle overlay for hand-drawn feel */}
			<g filter="url(#discover-squiggle)">
				<path
					d={d}
					// stroke="var(--color-accent)"
					stroke="var(--color-text-tertiary)"
					strokeOpacity={0.12}
					strokeWidth={1}
					fill="none"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeDasharray="8 6 4 9 5 7"
					vectorEffect="non-scaling-stroke"
				/>
			</g>
		</svg>
	);
};
